import { Model } from 'backbone';
import _ from 'lodash';

const
	hasBothSidePlayers = trade => _.has(trade, 'in') && _.has(trade, 'out'),
	hasOnlyOnePlayer = trade => trade.in && !trade.out || trade.out && !trade.in;

export class Trades extends Model {
	get defaults() {
		return {
			is_locked_action_reverse: true,
			is_locked_action_trade: true,
			trades: [
				{ is_confirmed: false, swap: [] },
				{ is_confirmed: false, swap: [] },
			],
			is_invalid_trade: false,
			is_valid_trade: false,
			is_show_trade_notice: false,
		}
	}

	initialize() {
		this.prefix = 'teams_classic/';
		this.url = 'show_trades';
	}

	reverseTrades(data) {
		this.url = 'reverse_trade';
		return this.getData({ type: 'reverse:trade', data });
	}

	rollbackTeam() {
		this.url = 'rollback';
		return this.getData({ type: 'rollback:team' });
	}

	get trade_type_options() {
		const options = {
			1: {
				url: 'make_trade',
				getTransformedData: () => {
					const trade = _.first(this.ready_for_trade);

					return {
						old_player_id: _.result(trade, 'out.id', 0),
						new_player_id: _.result(trade, 'in.id', 0),
						swap_player_ids: _.result(trade, 'swap', [])
					};
				}
			},
			2: {
				url: 'make_double_trade',
				getTransformedData: () => {
					const trades = this.ready_for_trade;

					return _.extend({}, ...trades.map((trade, i) => {
						const index = i + 1,
							pick = _.result.bind(_, trade);

						return {
							[`trade${index}_old_player_id`]: pick('out.id', 0),
							[`trade${index}_new_player_id`]: pick('in.id', 0),
							[`trade${index}_swap_player_ids`]: pick('swap', []),
						}
					}));
				}
			}
		};

		return options[_.size(this.ready_for_trade)];
	}

	makeTrade() {
		const { url, getTransformedData } = this.trade_type_options;

		this.url = url;

		return this.postData({
			data: getTransformedData(),
			type: 'make:trade'
		});
	}

	showTrade() {
		this.url = 'show_trades';
		return this;
	}

	setTradeHistoryUrl() {
		this.url = 'show_trades_history';
		return this;
	}

	setMakeTradeUrl() {
		this.url = 'make_trade';
		return this;
	}

	get trades() {
		return this.get('trades');
	}

	get confirmed_trades() {
		return this.trades.filter(_.matches({ is_confirmed: true }));
	}

	get not_confirmed_trades() {
		return this.trades.filter(_.matches({ is_confirmed: false }));
	}

	get ready_for_trade() {
		return this.not_confirmed_trades.filter(hasBothSidePlayers);
	}

	get with_trade_out_only() {
		return this.not_confirmed_trades.filter(trade => trade.out);
	}

	get not_ready_for_trade() {
		return this.not_confirmed_trades.filter(hasOnlyOnePlayer);
	}

	get is_success_trade_combination() {
		return _.size(this.ready_for_trade) && !_.size(this.not_ready_for_trade);
	}

	get can_make_reverse() {
		return Boolean(_.size(this.confirmed_trades));
	}

	get availbale_trade_spot_size() {
		return this.not_confirmed_trades.reduce(({ team, pool }, trade) => ({
			team: team + !trade.out,
			pool: pool + !trade.in
		}), { team: 0, pool: 0 });
	}

	getWorkableRepresentation(players_by_id = {}) {
		return _.values(_.omit(this.toJSON(), _.keys(this.defaults)))
			.map(({ new_player_id, old_player_id, swap_player_ids, id, is_confirmed = true }) => ({
				'in': _.result(players_by_id, new_player_id, { id: new_player_id }),
				'out': _.result(players_by_id, old_player_id, { id: old_player_id }),
				'swap': swap_player_ids,
				'is_confirmed': is_confirmed,
				'id': id,
			}))
			.concat(this.defaults.trades);
	}

	setSubstitutePlayer(id) {
		const trade = _.last(this.with_trade_out_only);

		if (trade) {
			trade.swap.push(id);
		}

		return this;
	}

	get team_players() {
		return _.chain(this.get('team').lineup)
			.values()
			.flatten()
			.filter(_.identity)
			.value();
	}

	get team_value() {
		const
			sum = (value, player) => value + _.result(player, 'cost', 0),
			trades = this.not_confirmed_trades,
			trade_in_players = trades.map(trade => trade.in).filter(_.identity),
			team_value = this.team_players.reduce(sum, 0),
			in_players_value = trade_in_players.reduce(sum, 0);

		return team_value + in_players_value;
	}

	get is_remaining_salary_valid() {
		return parseInt(this.get('remaining_salary'), 10) >= 0
	}

	get empty_positions() {
		return _(this.get('team').lineup)
			.map((value, key) => value.filter(_.negate(_.identity)).fill(key))
			.values()
			.flatten()
			.map(key => parseInt(key, 10) || key)
			.value()
	}

	get is_trade_positions_valid() {
		const trade_in_pos = this.ready_for_trade.map(
			trade => [...trade.in.positions, 'bench']
		);

		const positions_can_not_be_filled = this.empty_positions
			.reduce((empty_positions, pos, index) => {
				const
					possible_positions = trade_in_pos[index],
					equal_pos = _.first(_.intersection(empty_positions, possible_positions)),
					pos_index = empty_positions.indexOf(equal_pos);

				if (pos_index !== -1) {
					empty_positions.splice(pos_index, 1);
				}

				return empty_positions;
			}, [...this.empty_positions]);

		return !_.size(positions_can_not_be_filled);
	}
}